import * as React from "react";
import { Calendar as CalendarIcon, Clock } from "lucide-react";
import { formatInTimeZone, toZonedTime, fromZonedTime } from "date-fns-tz";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";

type DateTimePickerProps = {
  value: Date;
  onChange: (date: Date) => void;
  timezone?: string;
  placeholder?: string;
  minuteStep?: number;
  showTime?: boolean;
  use12Hours?: boolean;
  className?: string;
};

const buildTimeOptions = (step: number, use12Hours: boolean) => {
  const times: { value: string; label: string }[] = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += step) {
      const hour = String(h).padStart(2, "0");
      const minutes = String(m).padStart(2, "0");
      const value = `${hour}:${minutes}`;
      if (use12Hours) {
        const hourNum = h % 12 === 0 ? 12 : h % 12;
        const label = `${String(hourNum).padStart(2, "0")}:${minutes} ${h < 12 ? "AM" : "PM"}`;
        times.push({ value, label });
      } else {
        times.push({ value, label: value });
      }
    }
  }
  return times;
};

export function DateTimePicker({
  value,
  onChange,
  timezone = "UTC",
  placeholder = "Pick date & time",
  minuteStep = 15,
  showTime = true,
  use12Hours = false,
  className,
}: DateTimePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState<Date>(value);
  const timeOptions = React.useMemo(
    () => buildTimeOptions(minuteStep, use12Hours),
    [minuteStep, use12Hours]
  );

  React.useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const applyDate = (date: Date | undefined) => {
    if (!date) return;
    const zonedDay = toZonedTime(date, timezone);
    const current = internalValue ? toZonedTime(internalValue, timezone) : zonedDay;
    const mergedLocal = new Date(
      zonedDay.getFullYear(),
      zonedDay.getMonth(),
      zonedDay.getDate(),
      showTime ? current.getHours() : 0,
      showTime ? current.getMinutes() : 0,
      0,
      0
    );
    const result = fromZonedTime(mergedLocal, timezone);
    setInternalValue(result);
    onChange(result);
  };

  const applyTime = (time: string) => {
    const [hour, minute] = time.split(":").map(Number);
    const current = internalValue ? toZonedTime(internalValue, timezone) : toZonedTime(new Date(), timezone);
    const updatedLocal = new Date(
      current.getFullYear(),
      current.getMonth(),
      current.getDate(),
      hour,
      minute,
      0,
      0
    );
    const result = fromZonedTime(updatedLocal, timezone);
    setInternalValue(result);
    onChange(result);
  };

  const displayLabel = internalValue
    ? formatInTimeZone(
        internalValue,
        timezone,
        showTime
          ? use12Hours
            ? "PP · hh:mm a"
            : "PP · HH:mm"
          : "PP"
      )
    : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !internalValue && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {displayLabel}
          {timezone && (
            <span className="ml-auto text-xs text-muted-foreground uppercase tracking-wide">
              {timezone}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("p-4", showTime ? "w-auto" : "w-[320px]")} align="start">
        <div className={cn("flex", showTime ? "gap-4" : "")}>
          <Calendar
            mode="single"
            selected={internalValue}
            onSelect={applyDate}
            initialFocus
          />
          {showTime && (
            <div className="w-32">
              <div className="flex items-center gap-1 text-sm font-medium text-muted-foreground mb-2 px-1">
                <Clock className="h-4 w-4" />
                Time
              </div>
              <ScrollArea className="h-64 pr-2">
                <div className="grid gap-1">
                  {timeOptions.map(time => {
                    const isActive =
                      internalValue &&
                      formatInTimeZone(internalValue, timezone, "HH:mm") === time.value;
                    return (
                      <Button
                        key={time.value}
                        variant={isActive ? "default" : "ghost"}
                        size="sm"
                        className={cn(
                          "justify-start",
                          !isActive && "hover:bg-accent"
                        )}
                        onClick={() => applyTime(time.value)}
                      >
                        {time.label}
                      </Button>
                    );
                  })}
                </div>
              </ScrollArea>
              <p className="text-[11px] text-muted-foreground px-1 mt-2">
                Times shown in {timezone}
              </p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
